/**
 * Traversal abilities — the Metroidvania half of the movement package.
 *
 * See docs/02-movement.md §3 for the originals and §6 for the redesign that
 * fixed the four which were keys rather than mechanics.
 *
 * Three rules shape this whole file:
 *
 *  1. **Every ability is fun in an empty room.** If it only pays off against
 *     specific level geometry, it is a key. Each one here has a use with nothing
 *     around: Tether swings, Breach digs, Blink repositions, Burrow launches,
 *     Fluid Jet swims fast, Phase crosses, Updraft climbs.
 *  2. **Every ability has a material interaction.** They read the cell world and
 *     most of them write to it, so the simulation is the skill ceiling rather
 *     than a backdrop.
 *  3. **No ability may leave the body inside matter.** Four of them suspend
 *     collision or write a position directly, and nothing anywhere in the
 *     codebase un-embeds a body: a body that ends up overlapping solid cells is
 *     a permanent softlock, not a glitch the player can wiggle out of. Every
 *     such exit goes through `findFreeBox`, and refuses rather than materialise
 *     inside rock.
 *
 * One button drives all of them, context-selected (§6.5). Selection is strictly
 * ordered so the situational ability wins, every branch is gated on ownership so
 * an unowned ability falls through to the next, and — rule 5 of §6 — no branch
 * may shadow an ability the player also owns.
 */

import { clamp } from '../core/math';
import { aimAngleFor, type Input } from '../core/input';
import { Category, Flag, M, Tag, VOID } from '../sim/materials';
import { MAT } from '../sim/materialData';
import type { CellWorld } from '../sim/world';
import type { Particles } from '../sim/particles';
import { BodyFlag, World } from './ecs';
import { Ability, MoveState, type MoverState } from './movement';
import { fluidSpeedScale } from './movementTuning';

/** Which ability the context resolved to. Surfaced for the HUD. */
export const Chosen = {
  NONE: 0,
  FLUID_JET: 1,
  BURROW: 2,
  BREACH: 3,
  TETHER: 4,
  UPDRAFT: 5,
  PHASE: 6,
  BLINK: 7,
} as const;
export type ChosenId = (typeof Chosen)[keyof typeof Chosen];

export interface AbilityCtx {
  cells: CellWorld;
  particles: Particles | null;
  onEvent?: (msg: string) => void;
  onShake?: (amount: number) => void;
  /**
   * Visit every body an ability is allowed to act on, by ECS index.
   *
   * Supplied by the caller rather than queried here for the same reason
   * `MoveContext` takes its combat state as data: this module reads and writes
   * the component arrays it is handed, but it must not depend on the ECS query
   * layer to decide *which* rows exist. Absent means "nobody but the mover",
   * which is what the movement-only test rigs want.
   */
  forEachBody?: (fn: (idx: number) => void) => void;
}

// ---------------------------------------------------------------- tuning

const TETHER_RANGE = 150;
const TETHER_MIN_HARDNESS = 4;
const TETHER_MIN_DIST = 10;
/**
 * Tether needs a deliberate aim. A defaulted aim of `(facing, -0.6)` down a
 * 150-unit ray finds hard matter in essentially any interior, so Tether sat in
 * front of Updraft, Phase and Blink and made all three unreachable indoors —
 * §6 rule 5 with three victims.
 */
const TETHER_AIM_DEADZONE = 0.35;
const TETHER_REEL = 90;
const TETHER_SWING_ACCEL = 900;

const BREACH_SPEED = 430;
const BREACH_TIME = 0.34;
const BREACH_COOLDOWN = 0.75;
const BREACH_HARDNESS = 3;
const BREACH_RADIUS = 7;
/** How far ahead a horizontal charge looks for something worth charging at. */
const BREACH_LOOKAHEAD = 34;
const BREACH_EXIT_SEARCH = 26;

const BLINK_RANGE = 62;
const BLINK_MIN_RANGE = 4;
const BLINK_COOLDOWN = 0.55;
const BLINK_CHARGES = 2;
const BLINK_SNAPBACK_WINDOW = 1.2;
/**
 * Regen is deliberately longer than the snap-back window. When it was shorter,
 * the charge spent on a blink was already back before the window closed, so the
 * two-charge economy described in §6.3 never cost anything.
 */
const BLINK_REGEN = 1.45;
const BLINK_SNAPBACK_SEARCH = 14;

const BURROW_SPEED = 155;
const BURROW_EXIT_BOOST = 1.25;
/**
 * Loose cells that must be within reach before a burrow may start. Burrow
 * raises IGNORE_TERRAIN, so beginning one in a dusting of sand over stone is a
 * way to end up inside the stone; the volume gate is what makes "you can always
 * get back out" true rather than hopeful.
 */
const BURROW_MIN_VOLUME = 90;
const BURROW_PROBE_X = 6;
const BURROW_PROBE_DOWN = 10;
const BURROW_EXIT_SEARCH = 26;

const JET_IMPULSE = 520;
const JET_COOLDOWN = 0.16;
/** Matches `deepWater` in movement.ts — below it the SWIM clamps do not apply. */
const JET_MIN_SUBMERSION = 0.6;
const JET_EXPEL_CELLS = 3;

const PHASE_MAX_CELLS = 8;
const PHASE_COOLDOWN = 0.6;
const PHASE_FAIL_COOLDOWN = 0.25;
const PHASE_MIN_SPEED = 90;
/** How far ahead the obstacle may be. Phase is a crossup, not a homing jump. */
const PHASE_REACH = 5;
const PHASE_MAX_TIME = 0.5;
const PHASE_EXIT_SEARCH = 20;

const UPDRAFT_SCAN = 40;
const UPDRAFT_WIDTH = 5;
const UPDRAFT_MIN_HEAT = 180;
const UPDRAFT_TIME = 0.55;
const UPDRAFT_ACCEL = 2600;
const UPDRAFT_MAX_RISE = 420;
/** The thermal is spent by riding it — the column cools as it lifts you. */
const UPDRAFT_DRAIN = 520;
/**
 * How far the column reaches either side of the fire feeding it; it reaches
 * `UPDRAFT_SCAN` above. Anchored on the fire rather than on the player, because
 * the scan that *finds* the fire is centred on the player and the column is not:
 * anything standing over the blaze is in it, rider included or not.
 */
const UPDRAFT_COLUMN_HALF = 10;

// -------------------------------------------------------------- gravity

/**
 * Suspend gravity, remembering what was there. Hard-setting `gravityScale = 1`
 * on exit clobbers whatever else owned it — a hover attack, for instance, sets
 * it for the duration of a swing (`combat/wielder.ts`).
 */
function holdGravity(m: MoverState, w: World, i: number, scale: number): void {
  if (!m.gravityHeld) {
    m.savedGravity = w.gravityScale[i];
    m.gravityHeld = true;
  }
  w.gravityScale[i] = scale;
}

function releaseGravity(m: MoverState, w: World, i: number): void {
  if (!m.gravityHeld) return;
  w.gravityScale[i] = m.savedGravity;
  m.gravityHeld = false;
}

// ------------------------------------------------------------- geometry

function solidBox(cells: CellWorld, cx: number, cy: number, hw: number, hh: number): boolean {
  for (let y = Math.floor(cy - hh); y <= Math.floor(cy + hh); y++) {
    for (let x = Math.floor(cx - hw); x <= Math.floor(cx + hw); x++) {
      if (cells.isSolid(x, y)) return true;
    }
  }
  return false;
}

/**
 * Nearest position where the body's AABB fits in non-solid space, or null if
 * there is none within `maxRadius`.
 *
 * This is the shared precondition for every ability that stops ignoring
 * terrain (Burrow, Breach, Phase) or writes a position directly (Blink's
 * snap-back). `bias` orders the ring search so the body pops out the way it was
 * already travelling instead of backwards through the wall it just left.
 */
export function findFreeBox(
  cells: CellWorld,
  x: number,
  y: number,
  hw: number,
  hh: number,
  maxRadius = 24,
  biasX = 0,
  biasY = 0,
): { x: number; y: number } | null {
  if (!solidBox(cells, x, y, hw, hh)) return { x, y };
  const spokes = 16;
  // With no bias, prefer straight up: matter arrives from below far more often
  // than it does from above.
  const base = biasX !== 0 || biasY !== 0 ? Math.atan2(biasY, biasX) : -Math.PI / 2;
  for (let r = 2; r <= maxRadius; r += 2) {
    for (let k = 0; k < spokes; k++) {
      const step = Math.ceil(k / 2) * (k % 2 === 0 ? 1 : -1);
      const th = base + (step / spokes) * Math.PI * 2;
      const cx = x + Math.cos(th) * r;
      const cy = y + Math.sin(th) * r;
      if (!solidBox(cells, cx, cy, hw, hh)) return { x: cx, y: cy };
    }
  }
  return null;
}

/**
 * Matter that refuses translocation outright.
 *
 * docs/02 §3 row 8 says Phase "cannot phase through ADAMANT_SEAL or
 * VOID_MATTER", `worldgen.ts` stamps vault seals on the promise that there is
 * "no back way in", and RUNESTONE's own material entry says blink and phase
 * fail against it. None of that can be expressed as `isSolid`: VOID_MATTER is
 * plasma, so a solidity test reads a wall of it as open air and both abilities
 * cross it for free.
 */
function blocksTranslocation(mat: number): boolean {
  if (mat === VOID) return false;
  if (M.indestructible[mat] === 1) return true; // ADAMANT_SEAL
  if (M.temporalStability[mat] >= 1.2) return true; // RUNESTONE — anchors reality
  return (M.tags[mat] & Tag.VOID) !== 0; // VOID_MATTER and its kin
}

/**
 * Context selection. Strictly ordered: the more situational ability wins, and
 * each branch requires ownership so an unowned one falls through.
 */
export function chooseAbility(
  m: MoverState,
  w: World,
  i: number,
  cells: CellWorld,
  input: Input | null,
): ChosenId {
  const has = (bit: number) => (m.abilities & bit) !== 0;
  const down = input ? input.axis.y > 0.45 : false;
  const grounded = w.grounded[i] === 1;

  if (has(Ability.FLUID_ADAPT) && w.submersion[i] > JET_MIN_SUBMERSION) return Chosen.FLUID_JET;
  if (has(Ability.BURROW) && down && looseUnder(cells, w, i) && burrowable(cells, w, i)) {
    return Chosen.BURROW;
  }
  if (has(Ability.BREACH) && down && !grounded) return Chosen.BREACH;
  if (has(Ability.TETHER) && findAnchor(cells, w, i, input)) return Chosen.TETHER;
  if (has(Ability.UPDRAFT) && heatBelow(cells, w, i) > 0) return Chosen.UPDRAFT;
  // "Moving fast at a thin obstacle" (§6.5). Sealed matter still selects Phase
  // so the ability can say why it refused — a wall nothing in the kit can cross
  // has to read as a refusal, not as a dropped input. A wall that is merely too
  // thick falls through to whatever else is owned, which is more useful.
  if (has(Ability.PHASE_DRIFT) && Math.abs(w.vx[i]) > PHASE_MIN_SPEED) {
    if (phaseTarget(cells, w, i, Math.sign(w.vx[i]) || m.facing) !== null) return Chosen.PHASE;
  }
  // Breach's horizontal charge used to sit *below* an unconditional Blink
  // branch, which made it unreachable for anyone who owned Blink — and §3.1
  // puts Breach earlier on the critical path, so that was the guaranteed
  // acquisition order (§6 rule 5). It now wins whenever there is genuinely
  // something breakable in front of it, and Blink keeps the "otherwise" slot
  // §6.5 gives it.
  if (has(Ability.BREACH) && breakableAhead(cells, w, i, m)) return Chosen.BREACH;
  if (has(Ability.BLINK)) return Chosen.BLINK;
  if (has(Ability.BREACH)) return Chosen.BREACH;
  return Chosen.NONE;
}

/**
 * Who owns `grip` — issue #122, stated the way #87 and #78 were.
 *
 * ## The rule
 *
 *   **`grip` is a held grip on whatever is in reach: on a wall it is Cling, on
 *   the ground it is the attraction field. Neither reader consumes it.**
 *
 * ## Why one button carries two meanings, and why that is not #100
 *
 * #100 is the case where one input drove two verbs *at once*: `down` selected
 * the heavy attack and aimed it in the same frame, and the fix that worked was
 * to separate the verbs rather than arbitrate between them. The lesson is about
 * simultaneity, not about button count. Here the two readings are **mutually
 * exclusive states of the body**: `wantsGrip` in `movement.ts` requires wall
 * contact and `Ability.CLING`; this requires feet on the floor and no wall at
 * all. There is no frame in which both fire, so there is nothing to arbitrate —
 * which is exactly the shape `tests/downOwnership.test.ts` blesses (`down` is
 * read by six consumers and consumed by none; arbitration happens at the *other*
 * control).
 *
 * The alternative was the ability button, and it is worse for a reason §6 rule 5
 * of docs/02 already names: `chooseAbility` is strictly ordered and **no branch
 * may shadow an ability the player also owns**. Gathering's context is "there is
 * loose matter nearby", which in a falling-sand game is nearly always true, so
 * a high branch would shadow Updraft, Phase and Blink the way Tether once did,
 * and a low branch would be unreachable for anyone holding Blink. A verb whose
 * context is "almost always" does not belong in a context-selected slot.
 *
 * ## Why the field is not gated on an ability bit
 *
 * Every traversal ability here is a pickup. Gathering is not, because crafting
 * is priced from minute zero: ten recipes with material costs are readable and
 * quotable in the first room, and gating the only way to pay for them behind a
 * find would make the whole of `meta/crafting.ts` conditional on a drop. The
 * income side of an economy is not a Metroidvania key.
 */
export function gripMeansGather(m: MoverState, w: World, i: number): boolean {
  if (w.grounded[i] !== 1) return false;
  if (w.onWall[i] !== 0) return false;
  if (m.state === MoveState.CLING) return false;
  // A sustained ability owns the body while it runs; rooting in the middle of a
  // burrow or a phase would be two systems writing the same position.
  return !m.burrowing && !m.breaching && m.phaseTime <= 0 && !m.tetherActive;
}

/**
 * Advance ability state and consume the ability button.
 * Runs on the movement substep, after `stepMover`.
 */
export function updateAbilities(
  m: MoverState,
  w: World,
  i: number,
  input: Input | null,
  dt: number,
  ctx: AbilityCtx,
): void {
  tickAbilityTimers(m, dt);

  // The HUD previews the ability the button *would* fire, so `chosen` has to be
  // the live answer rather than the last one used (docs/02 §6.5: "the player
  // learns this button does the right thing here" is not learnable if the only
  // way to find out what is armed is to press it). During a sustained state the
  // answer is that state — the button's meaning while burrowing is "surface".
  //
  // Recomputed before the sustained-state returns below, so the preview is
  // correct in every branch and not only in the one that reaches the switch.
  m.chosen = m.breaching
    ? Chosen.BREACH
    : m.burrowing
      ? Chosen.BURROW
      : m.phaseTime > 0
        ? Chosen.PHASE
        : m.tetherActive
          ? Chosen.TETHER
          : chooseAbility(m, w, i, ctx.cells, input);

  // The updraft column is not exclusive — you can still blink or attack out of
  // it — so it is stepped before the sustained-state branches return.
  if (m.updraftTime > 0) stepUpdraft(m, w, i, dt, ctx);

  // --- sustained states run whether or not the button is pressed again ---
  if (m.breaching) {
    stepBreach(m, w, i, dt, ctx);
    return;
  }
  if (m.burrowing) {
    // The button cancels. Reading it here, before the sustained step returns,
    // is the whole point: the only other exit is `!surroundedByLoose`, which a
    // player who mis-timed a dive into a shallow bank can never reach on
    // purpose. An ability that suspends collision must always be leavable.
    if (input && input.consume('ability', 140)) {
      endBurrow(m, w, i, ctx, true);
    } else {
      stepBurrow(m, w, i, input, dt, ctx);
    }
    return;
  }
  if (m.phaseTime > 0) {
    stepPhase(m, w, i, dt, ctx);
    return;
  }
  if (m.tetherActive) {
    stepTether(m, w, i, input, dt, ctx);
    // Tether is a hold: releasing the button lets go.
    if (input && !input.held('ability')) m.tetherActive = false;
    return;
  }

  if (!input) return;
  const pressed = input.consume('ability', 140);
  if (!pressed) return;

  switch (m.chosen) {
    case Chosen.FLUID_JET:
      fireFluidJet(m, w, i, input, ctx);
      break;
    case Chosen.BURROW:
      startBurrow(m, w, i, ctx);
      break;
    case Chosen.BREACH:
      startBreach(m, w, i, input, ctx);
      break;
    case Chosen.TETHER:
      startTether(m, w, i, ctx);
      break;
    case Chosen.UPDRAFT:
      fireUpdraft(m, w, i, ctx);
      break;
    case Chosen.PHASE:
      firePhase(m, w, i, ctx);
      break;
    case Chosen.BLINK:
      fireBlink(m, w, i, input, ctx);
      break;
    default:
      break;
  }
}

function tickAbilityTimers(m: MoverState, dt: number): void {
  m.breachTime = Math.max(0, m.breachTime - dt);
  m.breachCooldown = Math.max(0, m.breachCooldown - dt);
  m.jetCooldown = Math.max(0, m.jetCooldown - dt);
  m.phaseCooldown = Math.max(0, m.phaseCooldown - dt);
  m.snapbackTime = Math.max(0, m.snapbackTime - dt);
  // `blinkCooldown` is ticked by movement.ts. It gates the regen so a spent
  // charge does not begin coming back during the blink's own recovery.
  if (m.blinkCooldown <= 0 && m.blinkCharges < BLINK_CHARGES) {
    m.blinkRegen -= dt;
    if (m.blinkRegen <= 0) {
      m.blinkCharges++;
      m.blinkRegen = BLINK_REGEN;
    }
  }
}

// ------------------------------------------------------------ fluid jet

/**
 * Expel liquid behind you for thrust. Thrust scales with the *density* of what
 * you are in, so mercury throws you and gas barely moves you — the material
 * table supplies the skill ceiling, and a flooded room becomes the fastest
 * terrain in the game instead of the slowest.
 */
function fireFluidJet(
  m: MoverState,
  w: World,
  i: number,
  input: Input,
  ctx: AbilityCtx,
): void {
  if (m.jetCooldown > 0) return;
  const mat = w.submergedMat[i];
  if (mat === VOID) return;

  let dx = input.axis.x;
  let dy = input.axis.y;
  if (dx === 0 && dy === 0) {
    dx = m.facing;
    dy = 0;
  }
  const len = Math.hypot(dx, dy) || 1;
  dx /= len;
  dy /= len;

  const scale = fluidSpeedScale(M.density[mat]);
  const thrust = JET_IMPULSE * scale;
  const before = Math.hypot(w.vx[i], w.vy[i]);
  w.vx[i] += dx * thrust * 0.016 * 60;
  w.vy[i] += dy * thrust * 0.016 * 60;

  // Cap what the jet itself can reach, at the same density-scaled ceiling
  // `applyVertical` clamps the swim to — one number, `fluidSpeedScale`, so the
  // two axes cannot drift apart again. Uncapped, the horizontal impulse made
  // this two different abilities depending on which way you aimed it: sideways
  // in mercury reached ~1200 u/s and coasted for twenty seconds off a 60 u/s²
  // decay. Momentum earned elsewhere is never stolen — hence the `before` term.
  const cap = Math.max(before, m.tuning.maxRunSpeed * scale);
  const speed = Math.hypot(w.vx[i], w.vy[i]);
  if (speed > cap) {
    const k = cap / speed;
    w.vx[i] *= k;
    w.vy[i] *= k;
  }
  m.jetCooldown = JET_COOLDOWN;

  // The expelled liquid is real: the cells behind you leave the world and are
  // thrown as matter. Previously this comment was aspirational — the ability
  // emitted decorative particles and never touched a cell, which made the one
  // ability named after the material system the only one that did not write to
  // it.
  const cells = ctx.cells;
  for (let k = 1; k <= JET_EXPEL_CELLS; k++) {
    const ex = Math.floor(w.x[i] - dx * (w.halfW[i] + k));
    const ey = Math.floor(w.y[i] - dy * (w.halfH[i] + k));
    if (cells.peek(ex, ey) !== mat) continue;
    cells.set(ex, ey, VOID);
    if (ctx.particles) ctx.particles.emit(ex, ey, mat, 2, 150, 0.4, -dx, -dy);
  }
  if (ctx.onShake) ctx.onShake(0.04);
}

// -------------------------------------------------------------- burrow

function looseUnder(cells: CellWorld, w: World, i: number): boolean {
  const y = Math.floor(w.y[i] + w.halfH[i] + 1);
  for (let dx = -2; dx <= 2; dx++) {
    const mat = cells.peek(Math.floor(w.x[i]) + dx, y);
    if (mat !== VOID && M.category[mat] === Category.LOOSE) return true;
  }
  return false;
}

/**
 * How much loose matter is actually within reach. Contact under the feet is not
 * enough: a burrow begun on a two-cell dusting sinks straight through it into
 * whatever is underneath, with collision suspended.
 */
function looseVolume(cells: CellWorld, w: World, i: number): number {
  const x0 = Math.floor(w.x[i] - w.halfW[i]) - BURROW_PROBE_X;
  const x1 = Math.floor(w.x[i] + w.halfW[i]) + BURROW_PROBE_X;
  const y0 = Math.floor(w.y[i] - w.halfH[i]);
  const y1 = Math.floor(w.y[i] + w.halfH[i]) + BURROW_PROBE_DOWN;
  let n = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const mat = cells.peek(x, y);
      if (mat !== VOID && M.category[mat] === Category.LOOSE) n++;
    }
  }
  return n;
}

function burrowable(cells: CellWorld, w: World, i: number): boolean {
  return looseVolume(cells, w, i) >= BURROW_MIN_VOLUME;
}

function startBurrow(m: MoverState, w: World, i: number, ctx: AbilityCtx): void {
  if (!burrowable(ctx.cells, w, i)) {
    if (ctx.onEvent) ctx.onEvent('burrow refused — not enough loose matter');
    return;
  }
  m.burrowing = true;
  w.bodyFlags[i] |= BodyFlag.IGNORE_TERRAIN;
  // Gravity off for the duration. With it on, a neutral stick lost the argument
  // between the 0.9 velocity damping and 1495 u/s² of fall gravity: the body
  // sank out of the sand it was swimming through and into the stone below,
  // where the burrow ended and IGNORE_TERRAIN cleared.
  holdGravity(m, w, i, 0);
  m.state = MoveState.BURROW;
  if (ctx.onEvent) ctx.onEvent('burrowed');
}

/**
 * Burrow moves *faster* than running, not slower. The original spec put it at
 * 88 u/s against a 145 u/s run, which made the ability a downgrade — the exact
 * "key, not a mechanic" failure docs/07 §1 identified.
 *
 * Exiting a surface launches you at travelling speed and throws the displaced
 * cells with you, so diving into a bank and erupting underneath something is a
 * single move rather than two.
 */
function stepBurrow(
  m: MoverState,
  w: World,
  i: number,
  input: Input | null,
  dt: number,
  ctx: AbilityCtx,
): void {
  const ix = input ? input.axis.x : 0;
  const iy = input ? input.axis.y : 0;
  const len = Math.hypot(ix, iy);
  if (len > 0.15) {
    w.vx[i] = (ix / len) * BURROW_SPEED;
    w.vy[i] = (iy / len) * BURROW_SPEED;
  } else {
    w.vx[i] *= 0.9;
    w.vy[i] *= 0.9;
  }

  // Eat the loose matter we pass through, and carry it.
  const cells = ctx.cells;
  const x0 = Math.floor(w.x[i] - w.halfW[i]);
  const x1 = Math.floor(w.x[i] + w.halfW[i]);
  const y0 = Math.floor(w.y[i] - w.halfH[i]);
  const y1 = Math.floor(w.y[i] + w.halfH[i]);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const mat = cells.peek(x, y);
      if (mat === VOID) continue;
      if (M.category[mat] !== Category.LOOSE) continue;
      cells.set(x, y, VOID);
      if (m.burrowPayload < 40) {
        m.burrowPayloadMat = mat;
        m.burrowPayload++;
      }
    }
  }
  void dt;

  // Surfacing: no loose matter left around us means we have broken out.
  if (!surroundedByLoose(cells, w, i)) endBurrow(m, w, i, ctx, false);
}

/**
 * End a burrow — or decline to.
 *
 * Clearing IGNORE_TERRAIN while the AABB overlaps solid cells strands the
 * player permanently, so the exit is conditional on there being somewhere to
 * exit *to*. When there is not, the burrow simply continues: still moving,
 * still steerable, which is a recoverable state rather than a dead run.
 */
function endBurrow(
  m: MoverState,
  w: World,
  i: number,
  ctx: AbilityCtx,
  cancelled: boolean,
): boolean {
  const cells = ctx.cells;
  const spot = findFreeBox(
    cells, w.x[i], w.y[i], w.halfW[i], w.halfH[i],
    BURROW_EXIT_SEARCH, w.vx[i], w.vy[i],
  );
  if (spot === null) return false;
  w.x[i] = spot.x;
  w.y[i] = spot.y;

  m.burrowing = false;
  w.bodyFlags[i] &= ~BodyFlag.IGNORE_TERRAIN;
  releaseGravity(m, w, i);

  if (cancelled) {
    // Surfacing under your own steam is the payoff; bailing out is not.
    w.vx[i] *= 0.5;
    w.vy[i] *= 0.5;
    m.burrowPayload = 0;
    if (ctx.onEvent) ctx.onEvent('surfaced');
    return true;
  }

  // Exit velocity — the launch is the payoff.
  w.vx[i] *= BURROW_EXIT_BOOST;
  w.vy[i] *= BURROW_EXIT_BOOST;
  if (ctx.particles && m.burrowPayload > 0) {
    ctx.particles.emit(
      w.x[i], w.y[i], m.burrowPayloadMat, Math.min(24, m.burrowPayload),
      200, 1.1, Math.sign(w.vx[i]) || 1, -0.4,
    );
  }
  m.burrowPayload = 0;
  if (ctx.onShake) ctx.onShake(0.12);
  if (ctx.onEvent) ctx.onEvent('erupted');
  return true;
}

/**
 * Are we still inside a body of loose matter?
 *
 * Sampled on a ring OUTSIDE the body, not around its centre. Burrowing eats the
 * cells it occupies, so a centre sample reads the tunnel the ability just dug
 * and concludes it has surfaced — the burrow ended on the substep after it
 * began, every time.
 */
function surroundedByLoose(cells: CellWorld, w: World, i: number): boolean {
  const rx = Math.ceil(w.halfW[i]) + 4;
  const ry = Math.ceil(w.halfH[i]) + 4;
  let loose = 0;
  let sampled = 0;
  for (let a = 0; a < 12; a++) {
    const th = (a / 12) * Math.PI * 2;
    const x = Math.floor(w.x[i] + Math.cos(th) * rx);
    const y = Math.floor(w.y[i] + Math.sin(th) * ry);
    const mat = cells.peek(x, y);
    sampled++;
    if (mat !== VOID && M.category[mat] === Category.LOOSE) loose++;
  }
  // Half the ring still buried counts as submerged. A strict majority makes
  // the exit trigger too late; a single sample makes it never trigger.
  return loose * 2 >= sampled;
}

// -------------------------------------------------------------- breach

/** Is there anything a horizontal charge could actually break, in front of us? */
function breakableAhead(cells: CellWorld, w: World, i: number, m: MoverState): boolean {
  const dir = m.facing || 1;
  const y = Math.floor(w.y[i]);
  for (let d = 1; d <= BREACH_LOOKAHEAD; d++) {
    const x = Math.floor(w.x[i] + dir * (w.halfW[i] + d));
    const mat = cells.peek(x, y);
    if (mat === VOID) continue;
    if (M.indestructible[mat] === 1) return false; // a wall, not a door
    if (M.hardness[mat] > BREACH_HARDNESS) return false;
    if (M.solid[mat] === 1) return true;
  }
  return false;
}

/**
 * A committed charge that destroys weak terrain and carries momentum THROUGH
 * it rather than stopping on it.
 *
 * Priced in tempo, per docs/06 §2: a real cooldown and a commitment window, so
 * it stays a traversal verb that happens to have a combat use rather than
 * becoming the drill-peephole exploit that broke Noita's combat.
 */
function startBreach(
  m: MoverState,
  w: World,
  i: number,
  input: Input,
  ctx: AbilityCtx,
): void {
  if (m.breachCooldown > 0) return;
  const down = input.axis.y > 0.45 && w.grounded[i] !== 1;
  m.breachDirX = down ? 0 : m.facing;
  m.breachDirY = down ? 1 : 0;
  m.breachTime = BREACH_TIME;
  m.breachCooldown = BREACH_TIME + BREACH_COOLDOWN;
  // The charge phases while it lasts. Relying on the destruction to outrun the
  // collision resolver does not work: the body is stopped by the wall on the
  // same substep the wall is being broken, so the charge stalls one cell short
  // of everything it is supposed to smash through.
  w.bodyFlags[i] |= BodyFlag.IGNORE_TERRAIN;
  holdGravity(m, w, i, 0);
  m.breaching = true;
  m.breachOriginX = w.x[i];
  m.breachOriginY = w.y[i];
  m.iframes = Math.max(m.iframes, 0.1);
  if (ctx.onShake) ctx.onShake(0.15);
  if (ctx.onEvent) ctx.onEvent(down ? 'breach slam' : 'breach charge');
}

function stepBreach(m: MoverState, w: World, i: number, dt: number, ctx: AbilityCtx): void {
  if (m.breachTime <= 0) {
    endBreach(m, w, i, ctx);
    return;
  }
  const cells = ctx.cells;
  const cx = Math.floor(w.x[i] + m.breachDirX * (w.halfW[i] + 3));
  const cy = Math.floor(w.y[i] + m.breachDirY * (w.halfH[i] + 3));

  // Stop dead against anything too hard to break. Phasing without this check
  // would turn Breach into a universal noclip, which is the drill-peephole
  // exploit docs/06 §2 exists to prevent.
  if (tooHardAhead(cells, cx, cy)) {
    endBreach(m, w, i, ctx);
    if (ctx.onShake) ctx.onShake(0.2);
    return;
  }

  w.vx[i] = m.breachDirX * BREACH_SPEED;
  w.vy[i] = m.breachDirY * BREACH_SPEED;

  // Order matters: break the wall first, THEN scatter. Brittle stone shatters
  // into its loose form (sandstone -> sand) which is still solid, so scattering
  // beforehand cleared nothing and the charge stalled against its own rubble.
  cells.damageCircle(cx, cy, BREACH_RADIUS, BREACH_HARDNESS);
  if (ctx.particles) ctx.particles.scatter(cx, cy, BREACH_RADIUS * 0.8, 190, 60);

  // Frozen matter shatters outright: this is the frost+impact combo's payoff.
  for (let dy = -BREACH_RADIUS; dy <= BREACH_RADIUS; dy += 2) {
    for (let dx = -BREACH_RADIUS; dx <= BREACH_RADIUS; dx += 2) {
      const x = cx + dx;
      const y = cy + dy;
      if (cells.getFlags(x, y) & Flag.FROZEN) cells.damageCell(x, y, 99);
    }
  }

  void dt;
}

/** Any unbreakable cell in the charge's path? */
function tooHardAhead(cells: CellWorld, cx: number, cy: number): boolean {
  for (let dy = -4; dy <= 4; dy += 2) {
    for (let dx = -2; dx <= 2; dx += 2) {
      const mat = cells.peek(cx + dx, cy + dy);
      if (mat === VOID) continue;
      if (M.indestructible[mat] === 1) return true;
      if (M.hardness[mat] > BREACH_HARDNESS) return true;
    }
  }
  return false;
}

function endBreach(m: MoverState, w: World, i: number, ctx: AbilityCtx): void {
  m.breachTime = 0;
  const cells = ctx.cells;
  const hw = w.halfW[i];
  const hh = w.halfH[i];
  m.breaching = false;

  // The charge phases, and the damage circle is centred *ahead* of the body, so
  // a slam ends with its trailing half still inside the bank — and unlike
  // Burrow, a charge cannot simply decline to end. Three fallbacks, in order.
  if (solidBox(cells, w.x[i], w.y[i], hw, hh)) {
    const spot = findFreeBox(
      cells, w.x[i], w.y[i], hw, hh, BREACH_EXIT_SEARCH, -m.breachDirX, -m.breachDirY,
    );
    if (spot !== null) {
      w.x[i] = spot.x;
      w.y[i] = spot.y;
    } else {
      // Breach is a destruction ability: the pocket it cannot find, it makes —
      // but only out of matter the charge was entitled to break.
      carveBox(cells, w.x[i], w.y[i], hw, hh, ctx.particles);
      if (solidBox(cells, w.x[i], w.y[i], hw, hh)) {
        w.x[i] = m.breachOriginX;
        w.y[i] = m.breachOriginY;
      }
    }
  }
  w.bodyFlags[i] &= ~BodyFlag.IGNORE_TERRAIN;
  releaseGravity(m, w, i);
  w.vx[i] *= 0.4;
  w.vy[i] *= 0.25;
}

/** Clear the breakable matter overlapping a box, throwing it as particles. */
function carveBox(
  cells: CellWorld,
  cx: number,
  cy: number,
  hw: number,
  hh: number,
  particles: Particles | null,
): void {
  for (let y = Math.floor(cy - hh); y <= Math.floor(cy + hh); y++) {
    for (let x = Math.floor(cx - hw); x <= Math.floor(cx + hw); x++) {
      const mat = cells.peek(x, y);
      if (mat === VOID) continue;
      if (M.indestructible[mat] === 1) continue;
      if (M.hardness[mat] > BREACH_HARDNESS) continue;
      cells.set(x, y, VOID);
      if (particles) particles.emit(x, y, mat, 1, 120, 0.6, 0, -1);
    }
  }
}

// -------------------------------------------------------------- tether

/**
 * Where the last `findAnchor` call landed. The `HOT` pattern from Updraft: the
 * result is written to one retained record rather than returned in a fresh
 * object, because the preview now asks this question on every substep and a
 * per-substep allocation is not something this file does.
 */
const ANCHOR = { x: 0, y: 0, found: false };

/**
 * The direction Tether throws its rope, as a unit vector, in the same eight-way
 * vocabulary every other directional thing in combat uses — issue #116.
 *
 * **It used to be the raw normalised stick**, and that was a second aim model.
 * `wielder.ts` sends every shot, sweep and terrain cut through `aimAngleFor`,
 * which snaps to five values — `NaN`, ±π/4, ±π/2 — and takes the horizontal
 * sense from `facing`; the eight directions are those five times the two
 * facings. On a keyboard the two models agree exactly. On a stick they diverge
 * by up to 22.5°, worst at the diagonals, which is precisely where a
 * ceiling-route grapple is thrown — so the ability grabbed at angles the rest
 * of the game's aim vocabulary cannot express, and the rope this now draws would
 * have been drawn to one of them.
 *
 * The snap is chosen rather than the analogue ray kept, and the reason is that
 * the rope is now visible: an eight-way *preview* that lands on a
 * continuously-aimed anchor is a HUD that lies, and the alternative — teaching
 * the aim vocabulary about continuous angles — is a change to `aimAngleFor`'s
 * five-value enumeration, which `input.ts` documents as load-bearing for the
 * skeleton poser and for weapon nodes authored against it.
 *
 * The magnitude deadzone stays on the *raw* stick, because it is answering a
 * different question — "did the player deliberately point at something" — and
 * it is what keeps Tether from shadowing Updraft, Phase and Blink indoors (§6
 * rule 5). Snapping first and testing the snapped vector would make every
 * horizontal drift a grapple.
 */
function tetherAimVector(w: World, i: number, input: Input): boolean {
  const ax = input.axis.x;
  const ay = input.axis.y;
  // The deadzone is also what keeps the live preview cheap: a neutral or
  // near-neutral stick — most of any run — never reaches the raycast at all.
  if (Math.hypot(ax, ay) < TETHER_AIM_DEADZONE) return false;
  const face = w.facing[i] < 0 ? -1 : 1;
  const a = aimAngleFor(ax, ay);
  // `NaN` is "no vertical held", which in the shared vocabulary means level —
  // and level is along facing, exactly as `fireOrSwing` reads it. The five
  // non-NaN values are already unit when built this way: `cos²+sin²` of ±π/4 is
  // 1 and of ±π/2 is 1, so there is nothing left to normalise.
  AIM.x = Number.isNaN(a) ? face : Math.cos(a) * face;
  AIM.y = Number.isNaN(a) ? 0 : Math.sin(a);
  return true;
}

/** The snapped throw direction, retained for the same reason `ANCHOR` is. */
const AIM = { x: 0, y: 0 };

/**
 * Is there an anchor under the aim?
 *
 * Requires a real aim input. Tether is the one ability whose selection context
 * is "you are pointing at something", and inferring that from `facing` made it
 * true almost everywhere indoors.
 */
function findAnchor(cells: CellWorld, w: World, i: number, input: Input | null): boolean {
  ANCHOR.found = false;
  if (!input) return false;
  if (!tetherAimVector(w, i, input)) return false;
  const dx = AIM.x;
  const dy = AIM.y;

  cells.raycast(w.x[i], w.y[i], dx, dy, TETHER_RANGE, (x, y, mat, dist) => {
    if (mat === VOID) return false;
    if (M.hardness[mat] < TETHER_MIN_HARDNESS) return false;
    // A wall you are already touching is not a grapple point.
    if (dist < TETHER_MIN_DIST) return true;
    ANCHOR.x = x;
    ANCHOR.y = y;
    ANCHOR.found = true;
    return true;
  });
  return ANCHOR.found;
}

/**
 * Attach to the anchor the preview already found.
 *
 * No second raycast: `updateAbilities` recomputed `m.chosen` at the top of this
 * same call, and reaching this branch means that computation ran `findAnchor`
 * and it succeeded. The live preview therefore costs a press frame *less* than
 * the old press-only selection did, which used to ray-cast once to choose and
 * again to attach.
 */
function startTether(m: MoverState, w: World, i: number, ctx: AbilityCtx): void {
  if (!ANCHOR.found) return;
  m.tetherActive = true;
  m.tetherX = ANCHOR.x;
  m.tetherY = ANCHOR.y;
  m.tetherLen = Math.hypot(ANCHOR.x - w.x[i], ANCHOR.y - w.y[i]);
  if (ctx.onEvent) ctx.onEvent('tethered');
}

/**
 * A real pendulum: the rope constrains distance and cancels only the radial
 * component of velocity, leaving the tangential component to swing. Reeling
 * changes the rope length, which is how a player pumps a swing.
 */
function stepTether(
  m: MoverState,
  w: World,
  i: number,
  input: Input | null,
  dt: number,
  ctx: AbilityCtx,
): void {
  const cells = ctx.cells;
  const dx = w.x[i] - m.tetherX;
  const dy = w.y[i] - m.tetherY;
  const d = Math.hypot(dx, dy);
  if (d < 1e-3) return;

  // The anchor can be destroyed out from under you — the world is destructible,
  // so a tether to a wall that no longer exists must let go.
  if (cells.peek(Math.floor(m.tetherX), Math.floor(m.tetherY)) === VOID) {
    m.tetherActive = false;
    return;
  }

  // …and so must a rope with terrain across it. There is no rope-vs-world
  // collision here (a wrapping rope is a much bigger feature), but without at
  // least severing it the length clamp below reels the body straight through
  // whatever the rope is lying across.
  if (!ropeClear(cells, w.x[i], w.y[i], m.tetherX, m.tetherY)) {
    m.tetherActive = false;
    if (ctx.onEvent) ctx.onEvent('tether severed');
    return;
  }

  const iy = input ? input.axis.y : 0;
  if (Math.abs(iy) > 0.2) m.tetherLen = clamp(m.tetherLen + iy * TETHER_REEL * dt, 8, TETHER_RANGE);

  const nx = dx / d;
  const ny = dy / d;

  // Swing input accelerates along the tangent, in the direction the player
  // actually pressed: project the pressed direction onto the tangent rather
  // than scaling it by `sign(ty)`, which flipped with the side of the anchor
  // you hung on — pressing Right directly below an anchor accelerated you left.
  const ix = input ? input.axis.x : 0;
  if (ix !== 0) {
    const tx = -ny;
    const ty = nx;
    const along = ix * tx; // (ix, 0) · (tx, ty)
    w.vx[i] += tx * along * TETHER_SWING_ACCEL * dt;
    w.vy[i] += ty * along * TETHER_SWING_ACCEL * dt;
  }

  if (d > m.tetherLen) {
    // Clamp to the rope — but the clamp is a position write, and a position
    // write that skips collision is a teleport. `updateAbilities` runs after
    // the body sweep, so anything it writes here is what the next substep
    // starts from: reel only as far as free space allows.
    const targetX = m.tetherX + nx * m.tetherLen;
    const targetY = m.tetherY + ny * m.tetherLen;
    const hw = w.halfW[i];
    const hh = w.halfH[i];
    let px = w.x[i];
    let py = w.y[i];
    const dist = Math.hypot(targetX - px, targetY - py);
    const steps = Math.max(1, Math.ceil(dist));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const cx = w.x[i] + (targetX - w.x[i]) * t;
      const cy = w.y[i] + (targetY - w.y[i]) * t;
      if (solidBox(cells, cx, cy, hw, hh)) break;
      px = cx;
      py = cy;
    }
    w.x[i] = px;
    w.y[i] = py;
    const radial = w.vx[i] * nx + w.vy[i] * ny;
    if (radial > 0) {
      w.vx[i] -= nx * radial;
      w.vy[i] -= ny * radial;
    }
  }
  m.state = MoveState.TETHER;
}

/** Is the straight line from body to anchor free of solid matter? */
function ropeClear(cells: CellWorld, x: number, y: number, ax: number, ay: number): boolean {
  const dx = ax - x;
  const dy = ay - y;
  const d = Math.hypot(dx, dy);
  if (d < 2) return true;
  let clear = true;
  // Stop short of the anchor: the anchor cell is solid by definition.
  cells.raycast(x, y, dx / d, dy / d, d - 1.5, (cx, cy) => {
    if (cells.isSolid(cx, cy)) {
      clear = false;
      return true;
    }
    return false;
  });
  return clear;
}

// ------------------------------------------------- tether, as the eye sees it

/**
 * Segments in the drawn rope. Nine, which is `visualForClass('whip', …)`'s own
 * count, so the grapple line reads as the same kind of object as the weapon it
 * is thrown from rather than as a HUD element that happens to be in the world.
 */
export const TETHER_ROPE_SEGMENTS = 9;

/**
 * The rope, hand to anchor — issue #116.
 *
 * `tetherX`, `tetherY`, `tetherActive` and `tetherLen` were declared in
 * `movement.ts`, written here, and **read nowhere in the renderer**: there was
 * no line, rope or chain on screen at any point during a tether. The only
 * weapon-ish thing drawn while hanging was `drawWeapons`'s whip *swing* — a
 * scripted crack along the body's forward axis computed from `swingAngle`, with
 * no knowledge of where the anchor is — and a ceiling anchor is overhead while
 * that crack goes forward. That right angle is the reported "the tether feels
 * perpendicular". It was never a rotation bug; it was a missing renderer, and
 * the thing the eye latched onto instead happens to be orthogonal in the case
 * the ability exists for.
 *
 * Writes `segments + 1` points as interleaved `x, y` into `out` and returns the
 * count. The first is the hand and **the last is the anchor cell exactly** —
 * this is a rope with two ends, not a ray of a given length, and a drawn rope
 * whose far end is a rounded multiple of a segment length would visibly miss
 * the thing it is tied to. `tests/tetherRope.test.ts` asserts the endpoint to
 * within a pixel over eight anchor directions.
 *
 * Straight, with no sag. `stepTether` is a rigid distance clamp and `ropeClear`
 * severs on the straight line, so a catenary would be drawing a physics the
 * simulation does not have.
 */
export function tetherRope(
  m: MoverState,
  handX: number,
  handY: number,
  segments: number,
  out: number[],
): number {
  const n = Math.max(1, segments);
  for (let k = 0; k <= n; k++) {
    const t = k / n;
    out[k * 2] = handX + (m.tetherX - handX) * t;
    out[k * 2 + 1] = handY + (m.tetherY - handY) * t;
  }
  return n + 1;
}

/**
 * Which way the weapon points while the rope is taut: at the anchor.
 *
 * The second half of #116. During `MoveState.TETHER` the drawn angle must come
 * from the anchor direction rather than from the swing pose, because the swing
 * pose is a forward crack and the anchor is usually overhead. World space and
 * already mirrored — there is no `facing` term, and that is the point: a rope
 * does not care which way you are looking.
 */
export function tetherDrawAngle(m: MoverState, handX: number, handY: number): number {
  return Math.atan2(m.tetherY - handY, m.tetherX - handX);
}

// ------------------------------------------------------------- updraft

/**
 * Hottest cell in the column below, and where it is. Updraft's fuel is fire the
 * player made, so the scan has to be as wide as a fire actually is: a single
 * pixel column meant a bonfire one cell off-centre read as nothing at all.
 */
const HOT = { x: 0, y: 0, temp: 0 };

function heatBelow(cells: CellWorld, w: World, i: number): number {
  let best = 0;
  const x0 = Math.floor(w.x[i] - w.halfW[i]) - UPDRAFT_WIDTH;
  const x1 = Math.floor(w.x[i] + w.halfW[i]) + UPDRAFT_WIDTH;
  const top = Math.floor(w.y[i] + w.halfH[i]);
  for (let x = x0; x <= x1; x++) {
    for (let d = 0; d < UPDRAFT_SCAN; d++) {
      const y = top + d;
      const t = cells.getTemp(x, y);
      if (t > best) {
        best = t;
        HOT.x = x;
        HOT.y = y;
      }
    }
  }
  HOT.temp = best;
  return best > UPDRAFT_MIN_HEAT ? best : 0;
}

/**
 * The design thesis in one ability: a traversal upgrade whose fuel is the
 * material simulation. You do not find an updraft — you set the floor on fire
 * and ride the thermal.
 *
 * It is a *column*, not an impulse (§3 row 9). As a one-shot velocity set it
 * was weaker than the jump button the player had at minute zero at every heat
 * short of open lava, which is rule 5 pointing the wrong way: the free ability
 * dominated the gated one.
 */
function fireUpdraft(m: MoverState, w: World, i: number, ctx: AbilityCtx): void {
  const heat = heatBelow(ctx.cells, w, i);
  if (heat <= 0) return;
  m.updraftTime = UPDRAFT_TIME;
  // A fire the player built (600–900 °C) has to out-climb a jump; a warm floor
  // must not. Sustained acceleration against gravity, not a velocity set.
  m.updraftPower = clamp((heat - UPDRAFT_MIN_HEAT) / 500, 0.5, 1.8);
  m.updraftTick = 0;
  m.dashesLeft = Math.max(m.dashesLeft, 1);
  if (ctx.onShake) ctx.onShake(0.06);
  if (ctx.onEvent) ctx.onEvent('rode the thermal');
}

function stepUpdraft(m: MoverState, w: World, i: number, dt: number, ctx: AbilityCtx): void {
  const cells = ctx.cells;
  const heat = heatBelow(cells, w, i);
  if (heat <= 0) {
    // Out of the column, or the fire is out. Either way the ride is over.
    m.updraftTime = 0;
    return;
  }
  m.updraftTime -= dt;

  w.vy[i] = Math.max(w.vy[i] - UPDRAFT_ACCEL * m.updraftPower * dt, -UPDRAFT_MAX_RISE);

  // Riding the thermal spends it: the source cools, so a column is a resource
  // the player built and can use up rather than a free elevator.
  const drain = -UPDRAFT_DRAIN * dt;
  cells.addTemp(HOT.x, HOT.y, drain);
  cells.addTemp(HOT.x - 1, HOT.y, drain * 0.5);
  cells.addTemp(HOT.x + 1, HOT.y, drain * 0.5);

  liftColumn(w, i, dt, m.updraftPower, ctx);

  m.updraftTick++;
  if (ctx.particles && m.updraftTick % 8 === 0) {
    ctx.particles.emit(w.x[i], w.y[i] + w.halfH[i], MAT.SMOKE, 3, 90, 0.5, 0, -1);
  }
}

/**
 * Everything else standing in the rising column goes up too (§3 row 9, "launch
 * enemies caught in the column").
 *
 * This is the line between a personal elevator and a crowd-control tool, and it
 * is what earns Updraft a slot next to Blink: the fire you built to climb out
 * of a room is the same fire that takes the room's floor out from under
 * everything on it.
 *
 * Lift is divided by mass rather than applied flat, so the column is a soft
 * counter to light swarms and barely inconveniences something heavy — the same
 * density argument buoyancy makes in `movement.ts`, one layer up.
 */
function liftColumn(
  w: World,
  self: number,
  dt: number,
  power: number,
  ctx: AbilityCtx,
): void {
  if (!ctx.forEachBody) return;
  const x0 = HOT.x - UPDRAFT_COLUMN_HALF;
  const x1 = HOT.x + UPDRAFT_COLUMN_HALF;
  const top = HOT.y - UPDRAFT_SCAN;
  ctx.forEachBody((e) => {
    // The rider's own lift is applied above, once, and at full strength: the
    // column is the ability, not a field the ability's owner also stands in.
    if (e === self) return;
    if (w.x[e] + w.halfW[e] < x0 || w.x[e] - w.halfW[e] > x1) return;
    if (w.y[e] + w.halfH[e] < top || w.y[e] - w.halfH[e] > HOT.y) return;
    const mass = Math.max(0.25, w.mass[e]);
    const rise = -UPDRAFT_MAX_RISE / mass;
    w.vy[e] = Math.max(w.vy[e] - (UPDRAFT_ACCEL * power * dt) / mass, rise);
  });
}

// --------------------------------------------------------------- phase

interface PhaseTarget {
  /** World x of the first free cell past the wall. */
  exitX: number;
  thickness: number;
  /** Matter in the path that refuses to be phased through at all. */
  blocked: boolean;
}

/**
 * The wall directly ahead, if it is one this ability can cross.
 *
 * `PHASE_MAX_CELLS` bounds the *thickness traversed*, not merely the loop: the
 * old probe started 11 cells out and took the first free pocket it found, so it
 * displaced the player 11–22 cells regardless of what the wall was, and could
 * put them on the far side of something 30 cells thick.
 */
function phaseTarget(
  cells: CellWorld,
  w: World,
  i: number,
  dir: number,
): PhaseTarget | null {
  const y = Math.floor(w.y[i]);
  const edge = w.x[i] + dir * w.halfW[i];

  let gap = 1;
  while (gap <= PHASE_REACH && !cells.isSolid(Math.floor(edge + dir * gap), y)) {
    if (blocksTranslocation(cells.peek(Math.floor(edge + dir * gap), y))) {
      return { exitX: 0, thickness: 0, blocked: true };
    }
    gap++;
  }
  if (gap > PHASE_REACH) return null;

  let t = 0;
  while (t <= PHASE_MAX_CELLS) {
    const x = Math.floor(edge + dir * (gap + t));
    if (blocksTranslocation(cells.peek(x, y))) {
      return { exitX: 0, thickness: t, blocked: true };
    }
    if (!cells.isSolid(x, y)) break;
    t++;
  }
  if (t > PHASE_MAX_CELLS) return null; // thicker than the ability crosses

  const exitX = edge + dir * (gap + t);
  const landing = exitX + dir * (w.halfW[i] + 1);
  if (solidBox(cells, landing, w.y[i], w.halfW[i], w.halfH[i])) return null;
  return { exitX, thickness: t, blocked: false };
}

/**
 * Crossup: pass through up to 8 cells of matter **preserving momentum**, with
 * no i-frames. Removing the invulnerability is the point — it was a
 * get-out-of-jail card that dominated Blink (rule 5). You go where you were
 * already going, faster than you could have gone around.
 *
 * It is a *state*, not a translation: `m.phaseTime` drives `MoveState.PHASED`
 * and `BodyFlag.PHASED` in movement.ts, and the body crosses under its own
 * velocity. Written as a teleport, it preserved momentum only by never using
 * it, and the two flags it was specified in terms of were dead code.
 */
function firePhase(m: MoverState, w: World, i: number, ctx: AbilityCtx): void {
  if (m.phaseCooldown > 0) return;
  const dir = Math.sign(w.vx[i]) || m.facing;
  const target = phaseTarget(ctx.cells, w, i, dir);

  if (target === null || target.blocked) {
    // Failure is feedback. Blink says "blink refused"; a silent no-op with no
    // cooldown and no event reads as a dropped input.
    m.phaseCooldown = PHASE_FAIL_COOLDOWN;
    if (ctx.onEvent) {
      ctx.onEvent(
        target !== null && target.blocked
          ? 'phase refused — sealed matter'
          : 'phase refused — no crossing',
      );
    }
    if (ctx.onShake) ctx.onShake(0.05);
    return;
  }

  const speed = Math.max(Math.abs(w.vx[i]), PHASE_MIN_SPEED);
  const travel = Math.abs(target.exitX - w.x[i]) + w.halfW[i] * 2 + 2;
  m.phaseOriginX = w.x[i];
  m.phaseOriginY = w.y[i];
  m.phaseBudget = PHASE_MAX_TIME;
  m.phaseTime = Math.min(PHASE_MAX_TIME, travel / speed + 0.02);
  m.phaseCooldown = PHASE_COOLDOWN;
  m.state = MoveState.PHASED;
  if (ctx.onEvent) ctx.onEvent('phasing');
}

function stepPhase(m: MoverState, w: World, i: number, dt: number, ctx: AbilityCtx): void {
  m.state = MoveState.PHASED;
  // Sealed matter stops a phase wherever it is met, mid-crossing included.
  if (boxBlocks(ctx.cells, w.x[i], w.y[i], w.halfW[i], w.halfH[i])) {
    endPhase(m, w, i, ctx, 'phase stopped — sealed matter');
    return;
  }
  // movement.ts ticks `phaseTime` down before this runs, so the last substep on
  // which we can still act is the one where it is about to reach zero.
  if (m.phaseTime <= dt) endPhase(m, w, i, ctx, 'phased through');
}

function endPhase(
  m: MoverState,
  w: World,
  i: number,
  ctx: AbilityCtx,
  msg: string,
): void {
  const cells = ctx.cells;
  const hw = w.halfW[i];
  const hh = w.halfH[i];

  if (solidBox(cells, w.x[i], w.y[i], hw, hh)) {
    const spot = findFreeBox(cells, w.x[i], w.y[i], hw, hh, PHASE_EXIT_SEARCH, w.vx[i], w.vy[i]);
    if (spot !== null) {
      w.x[i] = spot.x;
      w.y[i] = spot.y;
    } else if (m.phaseBudget > 0) {
      // Still inside matter with nowhere to land: keep phasing in small
      // increments rather than materialise inside it.
      m.phaseBudget -= 0.05;
      m.phaseTime = 0.05;
      return;
    } else {
      // Budget exhausted. The origin was free when we left it, so it is the
      // last-resort landing — Phase refuses a crossing rather than losing a run.
      w.x[i] = m.phaseOriginX;
      w.y[i] = m.phaseOriginY;
    }
  }

  m.phaseTime = 0;
  w.bodyFlags[i] &= ~BodyFlag.PHASED;
  if (ctx.onEvent) ctx.onEvent(msg);
}

/** Does the body's box overlap matter that refuses to be phased through? */
function boxBlocks(cells: CellWorld, cx: number, cy: number, hw: number, hh: number): boolean {
  for (let y = Math.floor(cy - hh); y <= Math.floor(cy + hh); y++) {
    for (let x = Math.floor(cx - hw); x <= Math.floor(cx + hw); x++) {
      if (blocksTranslocation(cells.peek(x, y))) return true;
    }
  }
  return false;
}

// --------------------------------------------------------------- blink

/**
 * Short-range teleport with **two charges** and a **snap-back**.
 *
 * It refuses visibly rather than dumping you inside rock — docs/10 §4 notes
 * Re-Logic's Rod of Discord cannot pass solid terrain at all, and our original
 * 12-cell version was strictly more permissive than the strongest movement item
 * in a game that had already thought about this.
 *
 * Re-blinking inside the window returns you to the residue you left, which
 * turns a commitment into something you can take back — a decision rather than
 * an escape. Snap-back is a blink and is priced like one: free snap-back, plus
 * regen shorter than the window, meant the second charge could never be spent.
 */
function fireBlink(
  m: MoverState,
  w: World,
  i: number,
  input: Input,
  ctx: AbilityCtx,
): void {
  const cells = ctx.cells;
  const hw = w.halfW[i];
  const hh = w.halfH[i];

  if (m.blinkCharges <= 0) {
    if (ctx.onEvent) ctx.onEvent('blink refused — no charge');
    return;
  }

  if (m.snapbackTime > 0) {
    // The destination is validated exactly as the forward blink's is: the world
    // is destructible and the origin you left may have collapsed behind you.
    const spot = findFreeBox(
      cells, m.snapbackX, m.snapbackY, hw, hh, BLINK_SNAPBACK_SEARCH, 0, -1,
    );
    if (spot === null) {
      m.snapbackTime = 0;
      if (ctx.onEvent) ctx.onEvent('snap-back refused — origin collapsed');
      return;
    }
    w.x[i] = spot.x;
    w.y[i] = spot.y;
    m.snapbackTime = 0;
    m.blinkCharges--;
    m.blinkCooldown = BLINK_COOLDOWN;
    m.blinkRegen = BLINK_REGEN;
    if (ctx.onEvent) ctx.onEvent('snapped back');
    return;
  }

  if (m.blinkCooldown > 0) {
    if (ctx.onEvent) ctx.onEvent('blink refused — recovering');
    return;
  }

  let dx = input.axis.x;
  let dy = input.axis.y;
  if (dx === 0 && dy === 0) {
    dx = m.facing;
    dy = 0;
  }
  const len = Math.hypot(dx, dy) || 1;
  dx /= len;
  dy /= len;

  const originX = w.x[i];
  const originY = w.y[i];

  // Walk outward a cell at a time and keep the furthest VALIDATED pocket. The
  // walk stops dead at sealed matter, so a vault wall is a wall: `isSolid` at
  // the destination alone let Blink step over a 3-cell ADAMANT_SEAL that
  // worldgen.ts stamps on the promise of "no back way in".
  let bestX = originX;
  let bestY = originY;
  let found = false;
  for (let d = 1; d <= BLINK_RANGE; d++) {
    const tx = originX + dx * d;
    const ty = originY + dy * d;
    if (blocksTranslocation(cells.peek(Math.floor(tx), Math.floor(ty)))) break;
    if (d < BLINK_MIN_RANGE) continue;
    if (!solidBox(cells, tx, ty, hw, hh)) {
      bestX = tx;
      bestY = ty;
      found = true;
    }
  }
  if (!found) {
    if (ctx.onEvent) ctx.onEvent('blink refused — no space');
    return;
  }

  // Blinking through matter chills it: the passage takes heat with it.
  const steps = Math.ceil(Math.hypot(bestX - originX, bestY - originY) / 2);
  for (let s = 0; s <= steps; s++) {
    const t = s / Math.max(1, steps);
    const x = Math.floor(originX + (bestX - originX) * t);
    const y = Math.floor(originY + (bestY - originY) * t);
    cells.addTemp(x, y, -60);
  }

  cells.set(Math.floor(originX), Math.floor(originY), MAT.TEMPORAL_RESIDUE);
  m.snapbackX = originX;
  m.snapbackY = originY;
  m.snapbackTime = BLINK_SNAPBACK_WINDOW;

  w.x[i] = bestX;
  w.y[i] = bestY;
  m.blinkCharges--;
  // Both timers, and they mean different things: `blinkCooldown` is the
  // recovery the HUD reads (it showed Blink permanently ready, at zero charges
  // included, because nothing ever wrote it), `blinkRegen` is the wait for the
  // spent charge to come back.
  m.blinkCooldown = BLINK_COOLDOWN;
  m.blinkRegen = BLINK_REGEN;
  m.iframes = Math.max(m.iframes, 0.06);
  if (ctx.onEvent) ctx.onEvent('blinked');
}

export const ABILITY_TUNING = {
  TETHER_RANGE,
  BREACH_SPEED,
  BLINK_RANGE,
  BLINK_CHARGES,
  BLINK_COOLDOWN,
  BLINK_REGEN,
  BLINK_SNAPBACK_WINDOW,
  BURROW_SPEED,
  BURROW_MIN_VOLUME,
  PHASE_MAX_CELLS,
  UPDRAFT_TIME,
};
